// Snabbsök title/section helpers — pure, self-contained string utilities split
// out of snabbsok.ts (phase-2 round 2, "simplified Option B"). These render and
// dedup the article/section parts of a chunk title (SECTION_VOCAB display
// cleanup, reference/date-suffix detection, last-dash article split). No I/O, no
// env, no React: safe to import from server code, the eval harness, AND the
// client label module (snabbsok-labels.ts). The original move was
// byte-identical to the snabbsok.ts cluster; since aug 2026 the file also
// OWNS two behaviours introduced by the display fixes -
// isNumberedClinicalHeading (IM sitelink carve-out predicate) and
// imDisplayLabel (the IM row-label rule, shared by render + FHM-2).

/**
 * Section-name vocabulary (P5) — used for *display* title cleanup only (see
 * `stripSectionSuffix`/`baseTitle`). Chunk titles are built by the pipeline as
 * "{article} – {section}", but many article names *contain* a dash
 * (Janusinfo "Slutenvård – behandlingsrekommendationer – …", RMR "… - regional
 * tillämpning … – …", Infektion.net "Virala CNS-infektioner - … – …"), so
 * stripping the LAST dash mangles those article names. We instead strip only
 * when the trailing segment is a recognised section label. Lower-cased; matched
 * exactly against the trailing segment, plus a couple of prefix patterns for
 * the "Vårdnivå och …" family. This list is intentionally conservative: an
 * unrecognised trailing segment is left intact (better to show a full article
 * name than to truncate it at a coincidental dash).
 *
 * NB: ranking/grouping logic (centrality, specificity penalty, sub-page
 * grouping) keeps the plain last-dash `splitArticleSection` — there the
 * trailing segment IS the section subject semantically (e.g. "Handinfektioner –
 * Herpes simplex" must score the query against "Handinfektioner", not the whole
 * string), so the conservative vocabulary split would over-count centrality.
 */
const SECTION_VOCAB = new Set<string>([
	// IM standard sections
	'icd-10',
	'icd',
	'sammanfattning',
	'bakgrund',
	'bakgrundsmaterial',
	'definition',
	'orsaker',
	'etiologi',
	'patogenes',
	'symtom',
	'symtom och kliniska fynd',
	'kliniska fynd',
	'differentialdiagnoser',
	'differentialdiagnostik',
	'utredning',
	'utredning/provtagning',
	'diagnostik',
	'diagnos',
	'behandling',
	'akut behandling',
	'behandlingsrekommendation',
	'behandlingsmål',
	'prevention',
	'profylax',
	'prognos',
	'komplikationer',
	'uppföljning',
	'klinisk uppföljning',
	'remiss',
	'remissrutiner',
	'handläggning',
	'nationella riktlinjer',
	'andra riktlinjer/ vårdprogram',
	// External (1177/VISS/region/Janusinfo/LV …) common section labels
	'inledning',
	'översikt',
	'vårdnivå och remissrutiner',
	'vårdnivå och samverkan',
	'rekommendationer',
	'patientinformation',
	'sjukskrivning',
	'referenser',
	'huvudbudskap', // LV behandlingsrekommendationer
	'beskrivning', // LV
	'om tillståndet', // 1177
	'om hälsotillståndet', // 1177
	// Tier-2.5: clean RCC/1177 section labels observed post-re-index.
	'kvalitetsuppföljning',
	'levnadsvanor',
	'omfattning av kunskapsstödet',
	'patientmedverkan och kommunikation',
	'sammanfattning av vårdförloppet',
	'flödesschema för vårdförloppet',
	'vårdnivå, samverkan och remissrutiner',
	// #15 sweep — niche-source section labels enumerated from the live index
	// (real section suffixes, not chunker fragments; verified per-source).
	// SoS FMB (försäkringsmedicinskt beslutsstöd) standard sections:
	'försäkringsmedicinsk information',
	'symtom, prognos och behandling',
	'funktionsnedsättning',
	'aktivitetsbegränsning',
	'rehabilitering',
	// SGF / VISS / region:
	'nationell riktlinje',
	'kloka listan',
	'kvalitetsindikatorer',
	'utredning enligt standardiserat vårdförlopp, svf',
	'behandlingsstöd',
	'vårdprogram',
	'publikationer',
	'vård i samverkan',
	// Skånelistan:
	'särskilda överväganden',
	'läkemedelsgrupper',
	'läkemedelsbehandling',
	'icke-farmakologisk behandling',
	// RMR:
	'läkemedel',
	// FoHM (smittskydd) standard sections:
	'vaccin',
	'symtom och behandling',
	'epidemiologisk övervakning och mikrobiologisk diagnostik',
	'orsakande mikroorganism, smittvägar och smittspridning',
	// Janusinfo "Studie: …" sections:
	'resultat',
	'studiedesign',
])
// Prefix patterns for section families whose label varies but starts with a
// stable phrase ("Vårdnivå och …", "Bilaga 2", "Appendix A").
const SECTION_PREFIXES = ['vårdnivå och ', 'bilaga', 'appendix']

/**
 * Tier-2.5: reference-list / appendix / metadata-noise chunk sections that the
 * 1177/RCC chunker surfaces as a "section" but which are NOT article content —
 * a numbered reference ("132. Burry L, Mehta S, …"), an appendix ("BILAGA 2"),
 * or kunskapsstyrning/date boilerplate. These must (a) be strippable at display
 * and (b) never be chosen as a page's representative when a real content chunk
 * exists (see repSectionRank).
 */
export function isReferenceSection(section: string): boolean {
	const s = section.trim()
	if (/^\d+\.\s/.test(s)) return true // "132. Burry L, …" numbered reference
	// BILAGA/APPENDIX anywhere (not just prefix): the chunker sometimes glues the
	// title before it, e.g. "Nationellt vårdprogram för hypertyreos   BILAGA 12".
	if (/\b(bilaga|appendix)\b/i.test(s)) return true
	if (/^(tabell|figur)\b/i.test(s)) return true
	if (/^referenser\b/i.test(s)) return true
	if (/nationellt system för kunskapsstyrning/i.test(s)) return true
	if (/nationell arbetsgrupp \(nag\)/i.test(s)) return true
	if (/beslutsdatum/i.test(s)) return true // 1177 "… Beslutsdatum …" metadata
	return false
}

/**
 * A section that ends in a revision date ("… 2023-01-17" / "… 2024"). Used to
 * DEPRIORITIZE such a chunk as a page representative (a content chunk wins) —
 * NOT to strip it (a real section could legitimately mention a year). The
 * date-suffixed *duplicate* title is handled separately by stripSectionSuffix's
 * dedup (normaliseForDedup).
 */
export function isDateSuffixedSection(section: string): boolean {
	return /\s20\d\d([-/]\d\d){0,2}\s*$/.test(section.trim())
}

/**
 * IM-only carve-out for numbered CLINICAL h3 headings ("2. Akut sarkoidos",
 * "1. Septisk artrit") so they stay valid sitelink targets — dropping them
 * left single-match rows with a raw #anchor main link (docs/snabbsok-section-
 * title-display-findings-2026-08.md).
 *
 * SCOPE (be honest about the breadth): at its call site, a true return
 * overrides EVERY branch of isReferenceSection for the section — not just the
 * `/^\d+\.\s/` rule. A hypothetical numbered reference-named heading
 * ("3. Referenser") would pass. Accepted deliberately: the corpus has zero
 * such sections (all 53 numbered IM sections are plain clinical headings),
 * and isDateSuffixedSection still applies independently at the call site.
 * The ≤60-char cap excludes glued boilerplate (the one real over-length
 * case); IM's own reference lists never surface as `N.`-numbered section
 * titles, so no citation-shape guards are needed. EXTERNAL sources keep the
 * broad rule untouched — their numbered sections ARE mostly literature
 * references, and an unscoped exemption regressed 54 external rows in the
 * 227-query sweep. The source gate lives at the sitelink call site (the only
 * isReferenceSection caller that changed).
 */
export function isNumberedClinicalHeading(section: string): boolean {
	const s = section.trim()
	return /^\d+\.\s/.test(s) && s.length <= 60
}

/**
 * The IM row display label: the pipeline-denormalized exact article name
 * (topic_label), falling back to whitelist stripping for older index data.
 * SINGLE source of truth for the rule - used by displayTitle (render path,
 * snabbsok-labels.ts) AND the FHM-2 lone-sitelink suppression (snabbsok.ts).
 * Editing the rule HERE keeps both consumers in sync.
 */
export function imDisplayLabel(
	topicLabel: string | undefined,
	title: string,
): string {
	return topicLabel?.trim() || baseTitle(title)
}

function isSectionLabel(seg: string): boolean {
	const s = seg.trim().toLowerCase()
	if (SECTION_VOCAB.has(s)) return true
	if (SECTION_PREFIXES.some(p => s.startsWith(p))) return true
	// Reference / appendix / boilerplate sections are also strippable suffixes.
	return isReferenceSection(seg)
}

/**
 * Split a chunk title at the LAST " – "/" - " separator. IM chunk titles follow
 * "Article – Section" so the prefix is the article subject and the suffix is the
 * section. Used by the RANKING/GROUPING logic (centrality, specificity penalty,
 * sub-page grouping, representative-section ranking) where the trailing segment
 * is semantically the section regardless of whether it's in our display
 * vocabulary. For DISPLAY title cleanup use `stripSectionSuffix` (P5-safe).
 */
export function splitArticleSection(title: string): { article: string; section: string } {
	const i1 = title.lastIndexOf(' – ')
	const i2 = title.lastIndexOf(' - ')
	const idx = Math.max(i1, i2)
	if (idx > 0) {
		return { article: title.slice(0, idx).trim(), section: title.slice(idx + 3).trim() }
	}
	return { article: title.trim(), section: '' }
}

/**
 * DISPLAY-only (P5): strip a trailing " – Section" suffix to surface the
 * article-level title, but ONLY when the trailing segment is a recognised
 * section label (SECTION_VOCAB). Article names that themselves contain a dash
 * are left intact. Safe for both IM and external sources.
 */
/**
 * Normalise a title segment for the dedup comparison: drop a trailing
 * ISO date / bare year and collapse internal whitespace. The 1177 PDF chunker
 * glues a revision date onto the repeated title, so the dup section reads
 * "Riktlinje för systemisk skleros    2023-01-17" — exact-equality dedup would
 * miss it. Lower-cased.
 */
export function normaliseForDedup(seg: string): string {
	return seg
		.replace(/\s+20\d\d([-/]\d\d){0,2}\s*$/, '') // trailing date / year
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase()
}

function stripSectionSuffix(title: string): string {
	const i1 = title.lastIndexOf(' – ')
	const i2 = title.lastIndexOf(' - ')
	const idx = Math.max(i1, i2)
	if (idx > 0) {
		const article = title.slice(0, idx).trim()
		const section = title.slice(idx + 3).trim()
		// Tier-2.5 dedup: the 1177 PDF chunker repeats the title as the section
		// (often with a trailing revision date / whitespace), so the stored title
		// is e.g. "Vårdprogram – Skörhet hos äldre – Skörhet hos äldre" or
		// "Riktlinje för systemisk skleros – Riktlinje för systemisk skleros
		// 2023-01-17". Drop the trailing segment when — after stripping a trailing
		// date and collapsing whitespace — it equals the article (2-seg) OR the
		// article's own trailing segment (3+-seg), preserving the rest.
		const prevSeg = splitArticleSection(article).section
		const sN = normaliseForDedup(section)
		if (
			isSectionLabel(section) ||
			sN === normaliseForDedup(article) ||
			(prevSeg && sN === normaliseForDedup(prevSeg))
		) {
			return article
		}
	}
	return title.trim()
}

/**
 * Strip a trailing " – Section" / " - Section" suffix to surface the
 * article-level title. Splits on the SECTION_VOCAB (P5), not the last dash, so
 * article names that themselves contain a dash (Janusinfo / RMR / Infektion.net
 * "Article-with-dash – Section") are not mangled. Safe to apply to BOTH IM and
 * external hits — an unrecognised trailing segment leaves the title intact
 * (P1: extends the section-strip from IM-only to all ~21,800 external docs).
 */
export function baseTitle(t: string): string {
	return stripSectionSuffix(t) || t.trim()
}

/**
 * Strip leading source name from chunk title since we render `source` as a
 * separate badge next to the link. E.g. "LMB Akutmedicin" → "Akutmedicin",
 * "SFAI Anestesi och intensivvård - …" → "Anestesi och intensivvård - …".
 * Falls back to the original title if the result would be empty.
 */
export function stripSourcePrefix(title: string, source: string): string {
	if (!source || !title) return title
	const escaped = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
	const re = new RegExp(`^${escaped}\\s*[:\\-–]?\\s+`, 'i')
	const stripped = title.replace(re, '').trim()
	return stripped || title
}
